import { forwardRef, useImperativeHandle, useMemo, useState } from "react";
import styled from "styled-components";
import { Autocomplete, Chip } from "@mui/material";
import { color, font, spacing } from "@runwayhealth/runway-components-react";

import useMedicationSuggestions from "@hooks/questionnaire/useMedicationSuggestions";

interface MedicationAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  // Fires per keystroke, unlike onChange (chip add/remove only) -- see the
  // docs/domains/medication-search-input/index.md for why a caller needs this.
  onInputChange?: (rawValue: string) => void;
}

export interface MedicationAutocompleteHandle {
  // Reads the pending typed text as committed, without waiting for blur --
  // see the docs/domains/medication-search-input/index.md for why a caller needs this.
  getCommittedValue: () => string;
}

const StatusText = styled.div<{ $isError: boolean }>`
  font-family: ${font.family};
  font-size: 0.8em;
  margin-top: 4px;
  color: ${(props) => (props.$isError ? color.feedback.error.dark : color.neutral.medium)};
`;

// Owns the border/background that the design system's TextField would
// normally put directly on its <input>. Chips render in a separate row
// below this box (see ChipsContainer), not inside it -- patients entering
// 4-6+ medications made the inline-chips layout too cramped to search in.
const InputContainer = styled.div`
  display: flex;
  align-items: center;
  min-height: 50px;
  padding: ${spacing.quarck} ${spacing.tiny};
  background-color: ${color.neutral.lightest};
  border: 1px solid ${color.neutral.light};
  border-radius: 4px;
  transition: 300ms;

  &:hover {
    border-color: ${color.brand.primary.medium};
  }
  &:focus-within {
    border-color: ${color.neutral.dark};
  }
`;

const ChipsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing.quarck};
  margin-top: ${spacing.tiny};
`;

const PlainInput = styled.input`
  flex: 1;
  min-width: 120px;
  border: none;
  outline: none;
  background: transparent;
  font-family: ${font.family};
  color: ${color.neutral.darkest};

  &::placeholder {
    color: ${color.neutral.medium};
  }
`;

// A plain, dependency-free "x" glyph -- @mui/icons-material isn't installed,
// and Chip only needs an element it can clone (adding its own onClick/
// className), not a specific icon library. Chip preserves this aria-label
// when cloning, so the delete affordance itself gets an accessible name
// (MUI's own default deleteIcon has none).
const RemoveGlyph = (props: React.SVGProps<SVGSVGElement>) => (
  <svg {...props} viewBox="0 0 24 24" width="18" height="18" fill="none">
    <path d="M6 6L18 18M6 18L18 6" stroke="currentColor" strokeWidth={2} strokeLinecap="round" />
  </svg>
);

// Splits on "|" only -- never JSON.parse. A legacy, pre-pipe-delimited
// free-text answer (no "|" present) naturally becomes a single-element
// array here, satisfying B9 without a special case.
const parseChips = (value: string): string[] =>
  value
    .split("|")
    .map((chip) => chip.trim())
    .filter(Boolean);

/**
 * Searchable, multi-select medication input -- see
 * docs/specs/medication-search-input.md. A new sibling to `AutocompleteInput`
 * (single-select, address-suggestion-coupled) and `MedicationSelector`
 * (unrelated post-diagnosis treatment picker); do not fold into either.
 */
const MedicationAutocomplete = forwardRef<MedicationAutocompleteHandle, MedicationAutocompleteProps>(
  ({ value, onChange, placeholder, onInputChange }, ref) => {
    const [inputValue, setInputValue] = useState("");
    const { suggestions, isLoading, hasError, search } = useMedicationSuggestions();

    const chips = useMemo(() => parseChips(value), [value]);

    const mergeChips = (newChips: string[]) =>
      Array.from(new Set(newChips.map((chip) => chip.trim()).filter(Boolean))).join("|");

    const commitChips = (newChips: string[]) => {
      onChange(mergeChips(newChips));
    };

    const commitPendingInput = () => {
      const pending = inputValue.trim();
      if (!pending) {
        return;
      }
      commitChips([...chips, pending]);
      setInputValue("");
    };

    useImperativeHandle(ref, () => ({
      getCommittedValue: () => {
        const pending = inputValue.trim();
        return mergeChips(pending ? [...chips, pending] : chips);
      },
    }));

    const statusMessage = isLoading
      ? "Searching…"
      : hasError
      ? "Search unavailable — you can still type and add a medication manually"
      : inputValue.trim() && suggestions.length === 0
      ? "No matches found"
      : inputValue.trim() && suggestions.length > 0
      ? `${suggestions.length} result${suggestions.length === 1 ? "" : "s"} found`
      : "";

    return (
      <div>
        <Autocomplete
          multiple
          freeSolo
          filterSelectedOptions
          options={suggestions}
          value={chips}
          inputValue={inputValue}
          onInputChange={(_event, newInputValue, reason) => {
            setInputValue(newInputValue);
            onInputChange?.(newInputValue);
            if (reason === "input") {
              search(newInputValue);
            }
          }}
          onChange={(_event, newValue, reason) => {
            commitChips(newValue as string[]);
            // Only clear the input on an actual addition (a selection or a
            // freeSolo Enter-commit) -- removing an unrelated chip (e.g. the
            // patient reconsiders an earlier entry while mid-typing a new one)
            // must never wipe out in-progress text (INV-2).
            if (reason === "selectOption" || reason === "createOption") {
              setInputValue("");
            }
          }}
          // Chips render separately below (ChipsContainer), not as MUI's
          // default startAdornment -- suppress the built-in inline rendering.
          renderTags={() => null}
          renderInput={(params) => (
            <InputContainer ref={params.InputProps.ref}>
              <PlainInput
                data-testid="medication-autocomplete-input"
                {...params.inputProps}
                placeholder={chips.length === 0 ? placeholder ?? "Search medications..." : undefined}
                onBlur={commitPendingInput}
              />
            </InputContainer>
          )}
        />
        {chips.length > 0 && (
          <ChipsContainer>
            {chips.map((chip) => (
              <Chip
                key={chip}
                label={chip}
                data-testid={`medication-chip-${chip}`}
                deleteIcon={<RemoveGlyph aria-label={`Remove ${chip}`} />}
                onDelete={() => commitChips(chips.filter((existing) => existing !== chip))}
                // Now a sibling of the Autocomplete's own root rather than
                // one of its internal tags, so it no longer benefits from
                // MUI's built-in blur guard -- without this, clicking delete
                // blurs the search input first, wrongly committing
                // in-progress typed text as a new chip (INV-2).
                onMouseDown={(e) => e.preventDefault()}
              />
            ))}
          </ChipsContainer>
        )}
        <StatusText
          $isError={hasError}
          aria-live="polite"
          data-testid="medication-autocomplete-status"
        >
          {statusMessage}
        </StatusText>
      </div>
    );
  }
);

MedicationAutocomplete.displayName = "MedicationAutocomplete";

export default MedicationAutocomplete;
